import { createLazyResource, createMutation, createTriggerResource, toast, useLocalization } from '@magicdoorinc/ui';
import { createMagicDoorContext } from '~/contexts/utils';
import { architecturalRequestRepository } from '~/repositories/architecturalRequestRepository';
import type { ArchitecturalRequestListFilter } from '~/repositories/architecturalRequestRepository';
import type { CreateBoardRequestDto } from '~/swagger/hoa/Api';

type DecideRequest = { id: string; note?: string };
type UpdateDueAtRequest = { id: string; dueAt?: string };

export const [ArchitecturalRequestsProvider, useArchitecturalRequests] = createMagicDoorContext('ArchitecturalRequests', () => {
  const { t } = useLocalization();
  const [filtered, setFilter, listActions] = createTriggerResource((filter: ArchitecturalRequestListFilter) =>
    architecturalRequestRepository.list(filter)
  );
  const [detail, getDetail, detailActions] = createTriggerResource((id: string) => architecturalRequestRepository.get(id));
  const [summary, summaryActions] = createLazyResource(() => architecturalRequestRepository.getSummary());

  const refetchAll = async () => {
    listActions.refetch();
    summaryActions.refetch();
    detailActions.refetch();
  };

  const runDetailMutation = async <T,>(runner: () => Promise<T>, successKey: string) => {
    try {
      const response = await runner();
      await refetchAll();
      toast(t(successKey), 'success');
      return response;
    } catch (error) {
      detailActions.refetch();
      throw error;
    }
  };

  const forcePmReview = createMutation((id: string) =>
    runDetailMutation(() => architecturalRequestRepository.forcePmReview(id), 'Request moved to PM review')
  );
  const approve = createMutation((payload: DecideRequest) =>
    runDetailMutation(() => architecturalRequestRepository.approve(payload.id, { note: payload.note }), 'Request approved')
  );
  const deny = createMutation((payload: DecideRequest) =>
    runDetailMutation(() => architecturalRequestRepository.deny(payload.id, { note: payload.note }), 'Request denied')
  );
  const updateDueAt = createMutation((payload: UpdateDueAtRequest) =>
    runDetailMutation(() => architecturalRequestRepository.updateDueAt(payload.id, { dueAt: payload.dueAt }), 'Due date updated')
  );
  const cancel = createMutation((id: string) => runDetailMutation(() => architecturalRequestRepository.cancel(id), 'Request cancelled'));
  const create = createMutation(async (payload: CreateBoardRequestDto) => {
    const response = await architecturalRequestRepository.create({ ...payload, files: [] });
    listActions.refetch();
    summaryActions.refetch();
    toast(t('Architectural request created successfully'), 'success');
    return response;
  });

  return {
    filtered,
    setFilter,
    refetch: listActions.refetch,
    detail,
    getDetail,
    detailActions,
    summary,
    refetchSummary: summaryActions.refetch,
    fetchSummary: summaryActions.fetch,
    forcePmReview,
    approve,
    deny,
    updateDueAt,
    cancel,
    create,
  };
});
